import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import styles from '../clip-template-mapper.module.scss';

import {
  ComponentBase,
  BaseStateProps,
  BaseDispatchProps,
  baseMapStateToProps,
  baseMapDispatchToProps,
} from 'components/componentBase';
import { FormId } from 'constants/formId';
import { ClippingPlatformState } from 'store/clippingPlatformState';
import ClipConfigurationForm from 'components/clipConfiguration/clipConfigurationForm';
import AppForm from 'components/form/appForm';
import { DataRequestActionTypes } from 'constants/actionTypes';
import { NormalizedClient } from 'models/normalized/normalizedClient';
import { createRequestActiveClientsAction } from 'actions/clientActions';
import { ApiDataTypes } from 'api/apiDataTypes';

type ClipTemplateMapperFormOwnProps = RouteComponentProps<{ id: string }>;

interface ClipTemplateMapperFormStateProps extends BaseStateProps {
  sessionToken: string;
  clipConfiguration?: string;
  client: NormalizedClient;
  clientsLoaded: boolean;
}

interface ClipTemplateMapperFormDispatchProps extends BaseDispatchProps {
  getClipTemplateMapped: (clientId: string, token?: string) => Promise<any>;
  loadClients: (c: ClipTemplateMapperForm) => void;
  clientId: string;
}

class ClipTemplateMapperForm extends ComponentBase<
  ClipTemplateMapperFormOwnProps,
  ClipTemplateMapperFormStateProps,
  ClipTemplateMapperFormDispatchProps
> {
  state = {
    clipConfigurationMapped: null as object,
    alreadyMapped: false,
    errorMessage: null as string,
  };

  protected async loadData() {
    const { clientId, sessionToken, clientsLoaded } = this.props;
    const { config, alreadyMapped, error } = await this.props.getClipTemplateMapped(clientId, sessionToken);
    this.setState({ clipConfigurationMapped: config, alreadyMapped, errorMessage: error });

    if (!clientsLoaded) {
      await this.props.loadClients(this);
    }
  }

  protected dataLoaded() {
    return this.state.clipConfigurationMapped !== null && this.props.clientsLoaded;
  }

  protected loggedInRender() {
    const {
      clientId,
      client: { clipConfiguration },
      history,
    } = this.props;

    const { alreadyMapped, clipConfigurationMapped, errorMessage } = this.state;

    return (
      <>
        {alreadyMapped && <h3>You configuration has been already mapped</h3>}
        {!alreadyMapped && errorMessage && <h3>The clip template could not be mapped</h3>}
        <div className={styles.clipTemplateMapperContainer}>
          <div className={styles.clipTemplateMapperForm}>
            <h2>Current configuration</h2>
            <AppForm
              form={FormId.ClipTemplateConfigPreview}
              hideButtons
              submitActionType={DataRequestActionTypes.EditClientRequested}
              initialValues={{
                clipConfiguration: JSON.parse(clipConfiguration),
              }}
            >
              <ClipConfigurationForm
                formId={FormId.ClipTemplateConfigPreview}
                customStyles={{ tabConfig: styles.clipTemplateTabConfig, container: styles.clipTemplateContainer }}
                readonly
              />
            </AppForm>
          </div>
          <div className={styles.clipTemplateMapperForm}>
            <h2>New configuration</h2>
            <AppForm
              form={FormId.UpdateClipTemplateConfig}
              submitActionType={DataRequestActionTypes.EditClientRequested}
              reduxData={{ clientId }}
              submitText="Approve"
              hideButtons={alreadyMapped}
              preprocessDataOnSubmit={this.preprocessDataOnSubmit}
              redirectToPathAfterSubmit="/clients"
              initialValues={{
                clipConfiguration: clipConfigurationMapped,
                clientId,
              }}
              onCancel={() => history.goBack()}
              disableSubmit={alreadyMapped}
            >
              <ClipConfigurationForm
                formId={FormId.UpdateClipTemplateConfig}
                customStyles={{ tabConfig: styles.clipTemplateTabConfig, container: styles.clipTemplateContainer }}
                readonly={alreadyMapped}
              />
            </AppForm>
          </div>
        </div>
      </>
    );
  }

  private preprocessDataOnSubmit(formData: any) {
    return { clipConfiguration: formData.clipConfiguration, mapped: true };
  }
}

const usersSelector = (state: ClippingPlatformState) => state.entities.clients;

const getClient = (state: ClippingPlatformState, id: string) => {
  const { byId } = usersSelector(state);

  return byId[id];
};

function mapStateToProps(
  state: ClippingPlatformState,
  ownProps: ClipTemplateMapperFormOwnProps,
): ClipTemplateMapperFormStateProps {
  const { id } = ownProps.match.params;

  return {
    ...baseMapStateToProps(state),
    client: getClient(state, id),
    clientsLoaded: state.appStatus.allLoaded[ApiDataTypes.ActiveClients],
    sessionToken: state.appStatus.session.sessionToken,
  };
}

function mapDispatchToProps(
  dispatch: Dispatch,
  ownProps: ClipTemplateMapperFormOwnProps,
): ClipTemplateMapperFormDispatchProps {
  const { id } = ownProps.match.params;

  return {
    ...baseMapDispatchToProps(dispatch),
    getClipTemplateMapped: async (clientId: string, token: string) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/clients/${clientId}/clip-template/new`, {
        method: 'get',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).then(res => res.json());

      return response;
    },
    loadClients: (c: ClipTemplateMapperForm) => dispatch(createRequestActiveClientsAction(c, dispatch)),
    clientId: id,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ClipTemplateMapperForm));
